<template>
  <div class="file-upload">
    <small v-if="label">
      {{ label }}
      <span v-if="required" class="color-red">*</span>
    </small>

    <div v-if="format === 'hash'" class="file-upload__plain">
      <input
        :value="hash"
        @input="$emit('update', $event.target.value)"
        class="file-upload__hash pr-4"
        :disabled="disabled || isLoading"
        aria-label="IPFS Hash"
        ref="input"
      />
      <button
        @click="format = 'files'"
        class="btn--link"
        aria-label="upload files"
        title="upload files"
        v-tippy
        type="button"
      >
        <MtxSvg icon="icon-upload" />
      </button>
    </div>

    <div
      v-else
      @drop="onDrop"
      @dragleave="isDroppable = false"
      @dragover.prevent="isDroppable = true"
      :class="{ 'file-upload__dropzone--droppable': isDroppable }"
      class="file-upload__dropzone flex text-center"
    >
      <MtxLoading v-if="isLoading" />

      <div
        v-else-if="hash"
        class="flex-grow flex flex-column justify-center align-center break-word"
      >
        IPFS hash:
        <br />
        <div>
          <span class="mr-1">{{ hash }}</span>
          <span class="mr-1 color-blue">
            <Copy plain :text="hash" />
          </span>
          <a
            :href="`https://ipfs.infura.io/ipfs/${hash}/`"
            class="btn btn--link mr-1"
            aria-label="Reset"
            target="_blank"
          >
            <MtxSvg icon="icon-link" />
          </a>
          <button @click="resetFiles" class="btn--link" type="button">
            <MtxSvg class="color-red" icon="icon-close" />
          </button>
        </div>
      </div>

      <div v-else-if="files" class="flex-grow flex flex-column justify-center">
        <p>
          You are about to upload
          {{ files.length > 1 ? `${files.length} files` : files[0].name }} to IPFS.
          <br />
          IPFS is a public and permanent file storage.
        </p>

        <div class="flex flex-row-reverse justify-center">
          <button @click="upload" class="btn--link mx-2" type="button">Continue</button>

          <button @click="files = null" class="btn--link mx-2" type="button">
            <span class="color-red">Cancel</span>
          </button>
        </div>
      </div>

      <label v-else class="flex-grow flex align-center justify-center">
        <input
          class="visually-hidden"
          @change="onChange"
          type="file"
          multiple
          :disabled="disabled || isLoading"
          aria-label="Files to upload to IPFS"
          ref="input"
        />
        <div class="file-upload__files-label">
          <MtxSvg icon="icon-upload" class="heading-1" />
          <div class="mb-2">
            Drop files or
            <span class="color-blue">click</span>
            to upload
          </div>

          <button @click="format = 'hash'" class="btn--link" type="button">
            <small>(or click here to use existing IPFS hash)</small>
          </button>
        </div>
      </label>
    </div>

    <small v-if="hash">
      <a :href="`https://ipfs.infura.io/ipfs/${hash}/`" target="_blank">
        Open hash on IPFS
      </a>
    </small>

    <small v-if="error" class="color-red">
      {{ error }}
    </small>
  </div>
</template>

<script>
import API from "@/api"
import errors from "@/data/errors"
import errorLogger from "@/utils/errorLogger"

export default {
  model: {
    prop: "hash",
    event: "update"
  },

  props: {
    hash: String,
    label: String,
    description: String,
    required: Boolean,
    disabled: Boolean
  },

  data: () => ({
    isLoading: false,
    error: null,
    format: "files",
    isDroppable: false,
    files: null // type: FileList | Null
  }),

  watch: {
    async format() {
      await this.$nextTick()
      this.$refs.input.focus()
    }
  },

  methods: {
    onDrop(event) {
      event.preventDefault()
      this.files = event.dataTransfer.files
      this.isDroppable = false
    },

    onChange(event) {
      const { files } = event.target
      this.files = files.length ? files : null
    },

    async upload() {
      if (this.isLoading || !this.files) return

      this.error = null

      try {
        this.isLoading = true

        const { success, data } = await API.uploadFiles(this.files)
        if (!success) throw new Error(errors.API_UNKNOWN)

        this.$emit("update", data.hash)
        this.files = null
      } catch (error) {
        this.error = "Error uploading to IPFS"
        this.$emit("error", error)
        errorLogger.log(error)
      } finally {
        this.isLoading = false
      }
    },

    async resetFiles() {
      this.$emit("update", "")
      this.files = null
      await this.$nextTick()
      this.$refs.input.focus()
    }
  }
}
</script>

<style lang="scss">
.file-upload {
  &__dropzone {
    height: rem(200px);
    border: 1px solid currentColor;
    border-radius: $borderRadius;
    border-color: $stroke;
    transition: color 0.3s ease;

    &--droppable {
      color: $highlight;
    }
  }

  input:focus + &__files-label {
    color: $highlight;
  }

  &__plain {
    position: relative;

    button {
      position: absolute;
      top: 50%;
      right: 1em;
      transform: translateY(-50%);
    }
  }
}
</style>
